import React, { useLayoutEffect, useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import { createPortal } from 'react-dom'
import FaIcon from '../FaIcon/FaIcon'
import { useAuth } from '../../../context/Auth'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { useBp } from '../../../utils/Breakpoints/Breakpoints'

import './Modal.scss'

const Modal = ({
  children,
  title,
  closeModal,
  autoCloseTimeout,
  dirty,
  modalTimer,
  error,
  customClass,
}) => {
  const [shake, setShake] = useState()
  const { currentUser } = useAuth()
  const focusRef = useRef()
  // eslint-disable-next-line no-unused-vars
  const [percent, setPercent] = useState(0)
  const { active: errorActive, count: errorCount } = error ?? {}

  const aboveXs = useBp('> xs')

  useLayoutEffect(() => {
    const lastScroll = window.scrollY
    document.documentElement.style.cssText = `top: -${window.scrollY}px; position: fixed; width: 100%;`
    document.documentElement.classList.add('no-scroll')

    return () => {
      document.documentElement.classList.remove('no-scroll')
      document.documentElement.removeAttribute('style')
      window.scrollTo(0, lastScroll)
    }
  }, [])

  // Don't shake when changing from X to 0 errorCount
  useEffect(() => {
    if (errorActive) {
      setShake(true)
    }
  }, [errorCount, errorActive])

  // Reset shake after timeout
  useEffect(() => {
    setTimeout(() => {
      setShake(false)
    }, 1000)
  }, [shake, errorActive])

  // Set initial focus on input
  useEffect(() => {
    if (aboveXs) focusRef.current.querySelector('input').focus()
  }, [focusRef, aboveXs])

  // Animate close button
  useEffect(() => {
    if (currentUser) {
      const endPercent = 100
      const duration = autoCloseTimeout // Duration in milliseconds
      const frameRate = 60 // Frames per second
      const totalFrames = (duration / 1000) * frameRate
      let frame = 0

      const animate = () => {
        const currentPercent = (frame / totalFrames) * endPercent
        setPercent(currentPercent)

        // Update the CSS variable for the gradient stop
        document.documentElement.style.setProperty(
          '--percent',
          `${currentPercent}%`
        )

        if (frame < totalFrames) {
          frame++
          requestAnimationFrame(animate)
        }
      }

      requestAnimationFrame(animate)

      // Cleanup function to stop the animation if the component unmounts
      return () => {
        frame = totalFrames // This will stop the animation
      }
    }
  }, [currentUser, autoCloseTimeout])

  const handleClickOutside = () => {
    dirty ? setShake(true) : closeModal()
  }

  const modalRef = useOutsideClick(
    (e, ref) =>
      ref.current && !ref.current.firstElementChild.contains(e.target),
    handleClickOutside
  )

  const [viewport, setViewport] = useState({
    maxHeight: '100vh',
    maxWidth: '100vw',
  })

  const [kbDetected, setKbDetected] = useState(false)
  const initialHeight = useRef(null)

  const updateViewport = () => {
    setViewport({
      maxHeight: window.visualViewport.height,
      maxWidth: window.visualViewport.width,
    })
  }

  useEffect(() => {
    const sizeDif = initialHeight.current - viewport.maxHeight
    const estKbHeight = 200
    if (sizeDif > estKbHeight && viewport.maxHeight < initialHeight.current) {
      setKbDetected(true)
    } else {
      setKbDetected(false)
    }
  }, [viewport.maxHeight])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.visualViewport !== 'undefined'
    ) {
      updateViewport()

      initialHeight.current = window.visualViewport.height

      window.visualViewport.addEventListener('resize', updateViewport)

      return () =>
        window.visualViewport.removeEventListener('resize', updateViewport)
    }
  }, [])

  return createPortal(
    <div
      className={`modal 
        ${dirty ? 'dirty' : ''} 
        ${errorActive ? 'error' : ''} 
        ${shake ? 'shake' : ''} 
        ${customClass ? customClass : ''}
        ${kbDetected ? 'kb-detected' : ''}`}
      style={{ height: viewport.maxHeight }}
      ref={(node) => {
        modalRef.current = node
        focusRef.current = node
      }}
      tabIndex={-1}>
      <div className='container'>
        <header className={kbDetected ? 'bar' : ''}>
          <h2>{title}</h2>
          <button
            className={`close-button ${autoCloseTimeout ? 'loading' : ''}`}
            onClick={() => {
              closeModal()
              clearTimeout(modalTimer)
            }}>
            <FaIcon name='xmark' size={20} />
          </button>
        </header>
        <div className='body'>{children}</div>
      </div>
    </div>,
    document.getElementById('app')
  )
}

Modal.defaultProps = {
  children: <span>No children</span>,
  title: 'Default',
  toggle: false,
}

Modal.propTypes = {
  children: propTypes.oneOfType([
    propTypes.array,
    propTypes.shape(),
    propTypes.string,
  ]),
  title: propTypes.string,
  closeModal: propTypes.func,
  autoCloseTimeout: propTypes.number,
  dirty: propTypes.bool,
  modalTimer: propTypes.number,
  error: propTypes.shape(),
  customClass: propTypes.string,
}

export default Modal
